import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { QueryParamsHandling, Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';

import { Search, SearchPrompt } from 'models';

import { Observable, from, of, switchMap } from 'rxjs';
import { encodeBase64 } from '../common/utility';
import {
  SearchPromptComponent,
  SearchPromptDialogData,
  SearchPromptResult,
} from '../components/search-prompt/search-prompt.component';
import { CommandService } from '../modules/command-palette';
import { SearchResultCacheService } from './search-result-cache.service';

const MIN_SEARCH_PROMPT_WIDTH = 400;

/** Search UI Service. */
@Injectable({
  providedIn: 'root',
})
export class SearchUIService {
  constructor(
    private logger: NGXLogger,
    private dialog: MatDialog,
    private commandService: CommandService,
    private searchResultCacheService: SearchResultCacheService,
    private router: Router
  ) {}

  /**
   * Add a search to the command palette.
   *
   * @param search Search.
   * @param databaseId DatabaseId.
   * @param archiveId Archive ID.
   * @param showPrompt Whether to show the prompt.
   * @param overwriteExisting Replaces the existing command if it exists.
   * @param customLabel Custom label for the search.
   * @param group Group label.
   * @param filterPrefix Optional filter prefix.
   */
  addToCommandPalette(
    search: Search,
    databaseId: number,
    archiveId: number,
    showPrompt: boolean,
    overwriteExisting: boolean,
    customLabel: string = '',
    group: string = 'Search',
    filterPrefix?: string | undefined
  ): void {
    const name = customLabel ? customLabel : search.name;
    if (overwriteExisting) {
      this.commandService.unregister(name, group);
    }

    this.commandService.register(
      name,
      'Run search',
      () =>
        showPrompt
          ? this.runWithPrompt(search, databaseId, archiveId)
          : this.redirectToSearch(databaseId, archiveId, search.id),
      group,
      filterPrefix
    );
  }

  /**
   * Redirect to the provided search.
   *
   * @param databaseId Database ID.
   * @param archiveId Archive ID.
   * @param searchId Search ID.
   * @param searchPrompts Search prompts.
   * @param queryParametersHandling Query parameters handling for the router.navigate function.
   */
  redirectToSearch(
    databaseId: number,
    archiveId: number,
    searchId: number,
    searchPrompts?: SearchPrompt[],
    queryParametersHandling: QueryParamsHandling | null = null
  ): void {
    this.redirectToSearch$(
      databaseId,
      archiveId,
      searchId,
      searchPrompts,
      queryParametersHandling
    ).subscribe();
  }

  /**
   * Opens a search in a new tab/window.
   *
   * @param databaseId Database id.
   * @param archiveId Archive id.
   * @param searchId Search id.
   * @param searchPrompts Optional search prompts.
   * @param queryParametersHandling Query parameter handling for the router.createUrlTree function.
   */
  openSearchInNewTab(
    databaseId: number,
    archiveId: number,
    searchId: number,
    searchPrompts?: SearchPrompt[],
    queryParametersHandling: QueryParamsHandling | null = null
  ) {
    const urlTree = this.getSearchUrlTree(
      databaseId,
      archiveId,
      searchId,
      searchPrompts,
      queryParametersHandling
    );
    const url = this.router.serializeUrl(urlTree);
    this.logger.debug('Opening search in new tab: ', url.toString());
    window.open(url, '_blank');
  }

  /**
   * Creates a url tree for the search.
   *
   * @param databaseId Database id.
   * @param archiveId Archive id.
   * @param searchId Search id.
   * @param searchPrompts Optional search prompts.
   * @param queryParametersHandling Query parameters handling for the router.navigate function.
   * @returns A url tree for the search.
   */
  private getSearchUrlTree(
    databaseId: number,
    archiveId: number,
    searchId: number,
    searchPrompts?: SearchPrompt[],
    queryParametersHandling: QueryParamsHandling | null = null
  ) {
    if (typeof searchPrompts !== 'undefined' && searchPrompts.length > 0) {
      const searchPromptString = encodeBase64(JSON.stringify(searchPrompts));
      return this.router.createUrlTree(
        ['db', databaseId, 'archive', archiveId, 'search', searchId],
        {
          queryParams: { prompts: searchPromptString },
          queryParamsHandling: queryParametersHandling,
        }
      );
    } else {
      // If no prompts are provided, just redirect to the search.
      return this.router.createUrlTree(
        ['db', databaseId, 'archive', archiveId, 'search', searchId],
        {
          queryParamsHandling: queryParametersHandling,
        }
      );
    }
  }

  /**
   * Redirect to the provided search.
   *
   * @param databaseId Database ID.
   * @param archiveId Archive ID.
   * @param searchId Search ID.
   * @param searchPrompts Search prompts.
   * @param queryParametersHandling Query parameters handling for the router.navigate function.
   *
   * @returns Observable that contains whether navigation was successful.
   */
  redirectToSearch$(
    databaseId: number,
    archiveId: number,
    searchId: number,
    searchPrompts?: SearchPrompt[],
    queryParametersHandling: QueryParamsHandling | null = null
  ): Observable<boolean> {
    const url = this.getSearchUrlTree(
      databaseId,
      archiveId,
      searchId,
      searchPrompts,
      queryParametersHandling
    );
    return from(this.router.navigateByUrl(url));
  }

  /**
   * Prompt for criteria and then run the search.
   *
   * @param search Search.
   * @param databaseId Database ID.
   * @param archiveId Archive ID.
   * @param searchPrompts Search prompts used to populate the dialog prompt. Overrides prompt values from url.
   * @param queryParametersHandling Query parameters handling for the router.navigate function.
   */
  runWithPrompt(
    search: Search,
    databaseId: number,
    archiveId: number,
    searchPrompts?: SearchPrompt[],
    queryParametersHandling: QueryParamsHandling | null = null
  ): void {
    this.runWithPrompt$(
      search,
      databaseId,
      archiveId,
      searchPrompts,
      queryParametersHandling
    ).subscribe();
  }

  /**
   * Prompt for criteria and then run the search.
   *
   * @param search Search.
   * @param databaseId Database ID.
   * @param archiveId Archive ID.
   * @param searchPrompts Search prompts used to populate the dialog prompt. Overrides prompt values from url.
   * @param queryParametersHandling Query parameters handling for the router.navigate function.
   *
   * @returns An observable which emits whether or not the search navigation was ran.
   */
  runWithPrompt$(
    search: Search,
    databaseId: number,
    archiveId: number,
    searchPrompts?: SearchPrompt[],
    queryParametersHandling: QueryParamsHandling | null = null
  ): Observable<boolean> {
    // Filter out static prompts.
    const userInteractivePrompts = search.parameters.filter(
      (parameter) => !parameter.value
    );

    if (
      userInteractivePrompts.length === 0 &&
      !search.settings.contentSearch.enabled // ContentSearch is never static so we always want to prompt if it's enabled.
    ) {
      this.logger.debug(
        'No user interactive prompts. Skipping prompts and running search...'
      );

      const searchPrompts = search.parameters.map((parameter) => ({
        id: parameter.id,
        prompt: parameter.prompt,
        value: parameter.value,
      }));
      return this.redirectToSearch$(
        databaseId,
        archiveId,
        search.id,
        searchPrompts,
        queryParametersHandling
      );
    }

    const promptDialogData: SearchPromptDialogData = {
      search,
      searchPrompts,
    };

    const promptDialog = this.dialog.open(SearchPromptComponent, {
      minWidth: MIN_SEARCH_PROMPT_WIDTH,
      data: promptDialogData,
    });

    return promptDialog.afterClosed().pipe(
      switchMap((result: SearchPromptResult) => {
        if (!result) {
          this.logger.debug('Search prompt dialog was cancelled.');
          return of(false);
        }

        this.logger.debug('Search prompt dialog was closed.', result);
        this.logger.debug('Clearing cached search results.');

        this.searchResultCacheService.deleteCacheResult(
          databaseId,
          archiveId,
          search.id,
          result.prompts
        );

        return this.redirectToSearch$(
          databaseId,
          archiveId,
          search.id,
          result.prompts,
          queryParametersHandling
        );
      })
    );
  }
}
